import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { X, ChevronLeft, ChevronRight, ShoppingCart, Minus, Plus, MessageCircle, Truck, MapPin, ChevronDown } from 'lucide-react';

const ProductQuickView = ({ product, isOpen, onClose }) => {
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(
    product?.variants?.length > 0 ? product.variants[0].name : null
  );

  if (!isOpen || !product) return null;

  const isCustom = product.custom_order === true;
  const hasVariants = product.variants && product.variants.length > 0;
  const hasCustomOption = product.has_custom_option === true;
  const images = product.images && product.images.length > 0 ? product.images : [];

  const activePrice = hasVariants && selectedVariant
    ? (product.variants.find(v => v.name === selectedVariant)?.price || product.price)
    : product.price;

  const handleAddToCart = async () => {
    setAdding(true);
    const success = await addToCart(product.id, quantity, selectedVariant);
    if (success) {
      setTimeout(() => { setAdding(false); setQuantity(1); }, 1000);
    } else {
      setAdding(false);
    }
  };

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="quick-view-modal">
      <div className="bg-[#F5F2EB] max-w-4xl w-full max-h-[90vh] overflow-y-auto rounded-sm relative" onClick={(e) => e.stopPropagation()} data-testid="quick-view-content">
        <button onClick={onClose} className="absolute top-4 right-4 bg-white/80 hover:bg-white p-2 rounded-full z-10 transition-colors" data-testid="quick-view-close">
          <X className="w-5 h-5 text-[#2C1810]" />
        </button>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
          {/* Image Section */}
          <div className="relative bg-[#EBE5DA] aspect-square md:aspect-auto md:min-h-[500px] flex items-center justify-center">
            {images.length > 0 ? (
              <>
                <img
                  src={images[currentImageIndex]}
                  alt={`${product.name} - ${currentImageIndex + 1}`}
                  className="w-full h-full object-contain p-4"
                />
                {images.length > 1 && (
                  <>
                    <button onClick={prevImage} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full" data-testid="qv-prev-image">
                      <ChevronLeft className="w-4 h-4 text-[#2C1810]" />
                    </button>
                    <button onClick={nextImage} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full" data-testid="qv-next-image">
                      <ChevronRight className="w-4 h-4 text-[#2C1810]" />
                    </button>
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                      {images.slice(0, 10).map((img, i) => (
                        <button key={`qv-dot-${img.slice(-20)}`} onClick={() => setCurrentImageIndex(i)}
                          className={`w-2 h-2 rounded-full transition-colors ${i === currentImageIndex ? 'bg-[#B87333]' : 'bg-white/60'}`} />
                      ))}
                      {images.length > 10 && <span className="text-xs text-white/60">+{images.length - 10}</span>}
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="text-[#8D7B68] text-sm">Product Image</div>
            )}
          </div>

          {/* Details Section */}
          <div className="p-6 md:p-8 flex flex-col">
            {/* Badges */}
            <div className="flex flex-wrap gap-1.5 mb-3">
              {product.free_shipping && (
                <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-green-50 text-green-700 border border-green-200 px-2 py-0.5 rounded-sm">
                  <Truck className="w-3 h-3" /> Free Shipping (US)
                </span>
              )}
              {product.local_only && (
                <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-sm">
                  <MapPin className="w-3 h-3" /> Local Pickup / Delivery Only
                </span>
              )}
            </div>

            <h2 className="text-2xl md:text-3xl font-medium mb-2" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="qv-product-name">
              {product.name.replace(/_/g, ' ')}
            </h2>

            {product.subtitle && (
              <p className="text-xs font-medium text-[#B87333] tracking-wide mb-3">{product.subtitle}</p>
            )}

            <p className="text-sm text-[#5D4037] leading-relaxed mb-4" data-testid="qv-description">
              {product.description}
            </p>

            <div className="mt-auto">
              {isCustom ? (
                <div className="space-y-3">
                  <p className="text-lg font-medium text-[#B87333]" style={{ fontFamily: 'Playfair Display, serif' }}>Custom Pricing</p>
                  <p className="text-xs text-[#8D7B68]">Contact us to discuss your vision and receive a personalized quote.</p>
                  <button onClick={() => { onClose(); navigate('/contact'); }}
                    className="w-full bg-[#2C1810] text-white px-3 py-3 rounded-sm hover:bg-[#3D2920] transition-colors flex items-center justify-center gap-2 text-sm"
                    data-testid="qv-contact-button">
                    <MessageCircle className="w-4 h-4" /> Contact for Custom Design
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-2xl font-semibold text-[#2C1810] mb-4" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="qv-price">
                    ${activePrice.toFixed(2)}
                  </p>

                  {hasVariants && (
                    <div className="mb-4" data-testid="qv-variant-selector">
                      <label className="block text-xs font-medium text-[#5D4037] mb-1">Select Option:</label>
                      <div className="relative">
                        <select value={selectedVariant || ''} onChange={(e) => setSelectedVariant(e.target.value)}
                          className="w-full appearance-none bg-white/60 border border-[#D7C0A0] px-3 py-2 pr-8 text-sm focus:outline-none focus:border-[#B87333] rounded-sm"
                          data-testid="qv-variant-dropdown">
                          {product.variants.map((v) => (
                            <option key={v.name} value={v.name}>{v.name} — ${v.price.toFixed(2)}</option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8D7B68] pointer-events-none" />
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <div className="flex items-center border border-[#D7C0A0] rounded-sm">
                      <button onClick={() => quantity > 1 && setQuantity(quantity - 1)} className="p-2 hover:bg-[#EBE5DA]" data-testid="qv-qty-minus">
                        <Minus className="w-4 h-4 text-[#2C1810]" />
                      </button>
                      <span className="w-10 text-center text-sm" data-testid="qv-qty-value">{quantity}</span>
                      <button onClick={() => quantity < 10 && setQuantity(quantity + 1)} className="p-2 hover:bg-[#EBE5DA]" data-testid="qv-qty-plus">
                        <Plus className="w-4 h-4 text-[#2C1810]" />
                      </button>
                    </div>
                    <button onClick={handleAddToCart} disabled={adding}
                      className="flex-1 bg-[#B87333] text-white px-3 py-2.5 rounded-sm hover:bg-[#A05A20] transition-colors flex items-center justify-center gap-2 text-sm"
                      data-testid="qv-add-to-cart">
                      <ShoppingCart className="w-4 h-4" />
                      {adding ? 'Added!' : 'Add to Cart'}
                    </button>
                  </div>

                  {hasCustomOption && (
                    <button onClick={() => { onClose(); navigate('/contact'); }}
                      className="w-full mt-3 border border-[#2C1810] text-[#2C1810] px-3 py-2 rounded-sm hover:bg-[#2C1810] hover:text-white transition-colors flex items-center justify-center gap-2 text-sm"
                      data-testid="qv-custom-option">
                      <MessageCircle className="w-4 h-4" /> Custom orders available
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductQuickView;
